import React from 'react';
import { makeStyles, Text, Badge, tokens } from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { SiteStatusCardProps, STATUS_COLORS, PRIORITY_LABELS, PriorityLevel } from '../types';

const useStyles = makeStyles({
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: '1px solid rgba(0,0,0,0.1)',
    minHeight: '85px',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadow4,
    },
  },
  cardCritical: {
    animation: 'pulse 2s infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px',
    gap: '4px',
  },
  siteName: {
    fontSize: '10px',
    fontWeight: '600',
    lineHeight: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  priorityBadge: {
    flexShrink: 0,
    fontSize: '8px',
    padding: '1px 4px',
    minHeight: 'auto',
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: '6px',
    flex: 1,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '9px',
    lineHeight: '12px',
  },
  infoLabel: {
    fontWeight: '400',
    opacity: 0.8,
  },
  infoValue: {
    fontWeight: '600',
    textAlign: 'right',
  },
  statusSection: {
    marginTop: 'auto',
    paddingTop: '6px',
    borderTop: '1px solid rgba(0,0,0,0.15)',
  },
  complianceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  complianceLabel: {
    fontSize: '8px',
    fontWeight: '500',
    opacity: 0.8,
  },
  complianceValue: {
    fontSize: '11px',
    fontWeight: '700',
  },
  warningIcon: {
    position: 'absolute',
    top: '4px',
    right: '4px',
  },
});

const getPriorityColor = (priority: string): 'danger' | 'warning' | 'success' | 'subtle' => {
  switch (priority) {
    case PriorityLevel.CRITICAL:
      return 'danger';
    case PriorityLevel.HIGH:
      return 'warning';
    case PriorityLevel.MEDIUM:
      return 'warning';
    default:
      return 'subtle';
  }
};

const getComplianceColor = (percentage: number, isLightBg: boolean): string => {
  if (percentage === 100) {
    return isLightBg ? '#0a5c0a' : '#90EE90';
  }
  if (percentage >= 50) {
    return isLightBg ? '#8B4513' : '#FFD700';
  }
  return isLightBg ? '#8B0000' : '#FF6B6B';
};

// Determine if background color is light (needs dark text) or dark (needs light text)
const getTextColors = (bgColor: string): { primary: string; secondary: string; border: string } => {
  const lightBackgrounds = ['#fde300', '#e0e0e0', '#f5f5f5', '#cccccc', '#d3d3d3'];
  const isLight = lightBackgrounds.some(c => bgColor.toLowerCase() === c.toLowerCase());

  if (isLight) {
    return {
      primary: '#1a1a1a',
      secondary: '#333333',
      border: 'rgba(0,0,0,0.2)',
    };
  }
  return {
    primary: '#ffffff',
    secondary: 'rgba(255,255,255,0.85)',
    border: 'rgba(255,255,255,0.3)',
  };
};

const isLightBackground = (bgColor: string): boolean => {
  const lightBackgrounds = ['#fde300', '#e0e0e0', '#f5f5f5', '#cccccc', '#d3d3d3'];
  return lightBackgrounds.some(c => bgColor.toLowerCase() === c.toLowerCase());
};

export const SiteStatusCard: React.FC<SiteStatusCardProps> = ({ site, onClick }) => {
  const styles = useStyles();

  const backgroundColor = STATUS_COLORS[site.status];
  const isCritical = site.priority === PriorityLevel.CRITICAL;
  const textColors = getTextColors(backgroundColor);
  const isLight = isLightBackground(backgroundColor);

  return (
    <div
      className={`${styles.card} ${isCritical ? styles.cardCritical : ''}`}
      style={{ backgroundColor, borderColor: textColors.border }}
      onClick={onClick}
    >
      {isCritical && (
        <WarningRegular
          className={styles.warningIcon}
          fontSize={14}
          style={{ color: isLight ? '#d13438' : '#FF6B6B' }}
        />
      )}

      <div className={styles.header}>
        <Text className={styles.siteName} style={{ color: textColors.primary }}>
          {site.site_name}
        </Text>
        {site.priority !== PriorityLevel.NORMAL && (
          <Badge
            appearance="filled"
            color={getPriorityColor(site.priority)}
            size="tiny"
            className={styles.priorityBadge}
          >
            {PRIORITY_LABELS[site.priority]}
          </Badge>
        )}
      </div>

      <div className={styles.infoSection}>
        <div className={styles.infoRow}>
          <Text className={styles.infoLabel} style={{ color: textColors.secondary }}>
            Code
          </Text>
          <Text className={styles.infoValue} style={{ color: textColors.primary }}>
            {site.site_code}
          </Text>
        </div>
        <div className={styles.infoRow}>
          <Text className={styles.infoLabel} style={{ color: textColors.secondary }}>
            Client
          </Text>
          <Text className={styles.infoValue} style={{ color: textColors.primary }}>
            {site.client_name}
          </Text>
        </div>
        <div className={styles.infoRow}>
          <Text className={styles.infoLabel} style={{ color: textColors.secondary }}>
            Shift
          </Text>
          <Text className={styles.infoValue} style={{ color: textColors.primary }}>
            {site.shift_name}
          </Text>
        </div>
        <div className={styles.infoRow}>
          <Text className={styles.infoLabel} style={{ color: textColors.secondary }}>
            Guards
          </Text>
          <Text className={styles.infoValue} style={{ color: textColors.primary }}>
            {site.clocked_in_guards_count}/{site.assigned_guards_count}
          </Text>
        </div>
        <div className={styles.infoRow}>
          <Text className={styles.infoLabel} style={{ color: textColors.secondary }}>
            Cycles
          </Text>
          <Text className={styles.infoValue} style={{ color: textColors.primary }}>
            {site.completed_cycles}/{site.required_clock_cycles}
          </Text>
        </div>
      </div>

      <div className={styles.statusSection} style={{ borderTopColor: textColors.border }}>
        <div className={styles.complianceRow}>
          <Text className={styles.complianceLabel} style={{ color: textColors.secondary }}>
            Compliance
          </Text>
          <Text
            className={styles.complianceValue}
            style={{ color: getComplianceColor(site.compliance_percentage, isLight) }}
          >
            {site.compliance_percentage.toFixed(0)}%
          </Text>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}
      </style>
    </div>
  );
};
